import { Injectable } from '@angular/core';

/**
 * Serviço para gerar dados fake para preview de DataGrids
 * Útil durante configuração no Builder para visualizar como ficará
 */
@Injectable({
  providedIn: 'root'
})
export class FakeDataGeneratorService {

  private firstNames = ['João', 'Maria', 'Pedro', 'Ana', 'Carlos', 'Juliana', 'Ricardo', 'Fernanda', 'Lucas', 'Beatriz'];
  private lastNames = ['Silva', 'Santos', 'Oliveira', 'Souza', 'Costa', 'Pereira', 'Rodrigues', 'Almeida', 'Nascimento', 'Lima'];
  private companies = ['TechCorp', 'DataSys', 'CloudNet', 'WebSolutions', 'DevTeam', 'CodeLab', 'SoftWorks', 'InnoTech', 'ByteForce', 'AppMakers'];
  private products = ['Notebook', 'Mouse', 'Teclado', 'Monitor', 'Webcam', 'Headset', 'SSD', 'Memória RAM', 'Placa de Vídeo', 'Processador'];
  private categories = ['Eletrônicos', 'Vestuário', 'Alimentos', 'Livros', 'Brinquedos', 'Móveis', 'Decoração', 'Ferramentas', 'Esportes', 'Saúde'];
  private cities = ['São Paulo', 'Rio de Janeiro', 'Belo Horizonte', 'Brasília', 'Curitiba', 'Porto Alegre', 'Salvador', 'Fortaleza', 'Recife', 'Manaus'];
  private status = ['Ativo', 'Inativo', 'Pendente', 'Aprovado', 'Cancelado', 'Em Análise', 'Concluído', 'Aguardando'];

  /**
   * Gera um array de dados fake baseado nas colunas
   * @param columns Colunas do DataGrid
   * @param rowCount Quantidade de linhas a gerar (padrão: 10)
   * @returns Array de objetos com dados fake
   */
  generateFakeData(columns: any[], rowCount: number = 10): any[] {
    const data: any[] = [];

    for (let i = 0; i < rowCount; i++) {
      const row: any = {};
      
      columns.forEach(column => {
        row[column.field] = this.generateValueByType(column.type, column.field, i);
      });

      data.push(row);
    }

    return data;
  }

  /**
   * Gera um valor fake baseado no tipo da coluna
   */
  private generateValueByType(type: string, fieldName: string, index: number): any {
    const lowerField = fieldName.toLowerCase();

    // Tentar inferir pelo nome do campo primeiro
    if (lowerField.includes('id')) {
      return index + 1;
    }
    
    if (lowerField.includes('name') || lowerField.includes('nome')) {
      return this.generateName();
    }
    
    if (lowerField.includes('email')) {
      return this.generateEmail();
    }
    
    if (lowerField.includes('phone') || lowerField.includes('telefone') || lowerField.includes('celular')) {
      return this.generatePhone();
    }
    
    if (lowerField.includes('company') || lowerField.includes('empresa')) {
      return this.random(this.companies);
    }
    
    if (lowerField.includes('product') || lowerField.includes('produto')) {
      return this.random(this.products);
    }
    
    if (lowerField.includes('category') || lowerField.includes('categoria')) {
      return this.random(this.categories);
    }
    
    if (lowerField.includes('city') || lowerField.includes('cidade')) {
      return this.random(this.cities);
    }
    
    if (lowerField.includes('status')) {
      return this.random(this.status);
    }
    
    if (lowerField.includes('price') || lowerField.includes('preco') || lowerField.includes('valor')) {
      return this.generatePrice();
    }
    
    if (lowerField.includes('stock') || lowerField.includes('estoque') || lowerField.includes('quantidade')) {
      return this.randomInt(0, 100);
    }

    // Se não inferiu pelo nome, usar o tipo
    switch (type) {
      case 'number':
        return this.randomInt(1, 1000);
      
      case 'boolean':
        return Math.random() > 0.5;
      
      case 'date':
        return this.generateDate();
      
      case 'text':
      default:
        return this.generateText(fieldName);
    }
  }

  /**
   * Gera nome completo fake
   */
  private generateName(): string {
    const firstName = this.random(this.firstNames);
    const lastName = this.random(this.lastNames);
    return `${firstName} ${lastName}`;
  }

  /**
   * Gera email fake baseado em nome
   */
  private generateEmail(): string {
    const firstName = this.random(this.firstNames).toLowerCase();
    const domain = ['gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'empresa.com.br'];
    return `${firstName}${this.randomInt(100, 999)}@${this.random(domain)}`;
  }

  /**
   * Gera telefone brasileiro fake
   */
  private generatePhone(): string {
    const ddd = this.randomInt(11, 99);
    const prefix = this.randomInt(90000, 99999);
    const suffix = this.randomInt(1000, 9999);
    return `(${ddd}) ${prefix}-${suffix}`;
  }

  /**
   * Gera preço fake
   */
  private generatePrice(): number {
    return Math.round(Math.random() * 5000 * 100) / 100;
  }

  /**
   * Gera data fake (últimos 365 dias)
   */
  private generateDate(): string {
    const daysAgo = this.randomInt(0, 365);
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD
  }

  /**
   * Gera texto genérico baseado no nome do campo
   */
  private generateText(fieldName: string): string {
    const words = ['Lorem', 'Ipsum', 'Dolor', 'Sit', 'Amet', 'Consectetur', 'Adipiscing', 'Elit'];
    const wordCount = this.randomInt(2, 4);
    const selectedWords = [];
    
    for (let i = 0; i < wordCount; i++) {
      selectedWords.push(this.random(words));
    }
    
    return selectedWords.join(' ');
  }

  /**
   * Retorna elemento aleatório de um array
   */
  private random<T>(array: T[]): T {
    return array[Math.floor(Math.random() * array.length)];
  }

  /**
   * Gera número inteiro aleatório entre min e max (inclusive)
   */
  private randomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * Gera dados fake a partir de um objeto de exemplo
   * Útil quando o usuário cola um JSON - replicar a estrutura
   */
  generateFromSample(sampleObject: any, rowCount: number = 10): any[] {
    const data: any[] = [];

    for (let i = 0; i < rowCount; i++) {
      const row = this.cloneAndRandomize(sampleObject, i);
      data.push(row);
    }

    return data;
  }

  /**
   * Clona objeto e randomiza valores mantendo tipos
   */
  private cloneAndRandomize(obj: any, index: number): any {
    const cloned: any = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        const type = typeof value;

        if (type === 'number') {
          // Se parece com ID, incrementar; senão randomizar
          if (key.toLowerCase().includes('id')) {
            cloned[key] = index + 1;
          } else {
            cloned[key] = this.randomInt(1, Math.max(value * 2, 100));
          }
        } else if (type === 'boolean') {
          cloned[key] = Math.random() > 0.5;
        } else if (type === 'string') {
          // Tentar manter formato similar
          if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
            cloned[key] = this.generateDate();
          } else if (value.includes('@')) {
            cloned[key] = this.generateEmail();
          } else if (value.match(/^\(\d{2}\)/)) {
            cloned[key] = this.generatePhone();
          } else {
            cloned[key] = this.generateValueByType('text', key, index);
          }
        } else if (type === 'object' && value !== null) {
          // Recursivamente clonar objetos aninhados
          cloned[key] = this.cloneAndRandomize(value, index);
        } else {
          cloned[key] = value;
        }
      }
    }

    return cloned;
  }
}





